import { useState } from "react";
import { ArrowRight, ExternalLink, GraduationCap, Trophy, Users, MapPin, Calendar, ShieldCheck, Sparkles } from "lucide-react";
import { useContent, useSite } from "../context/SiteContext";
import { mediaUrl } from "../lib/api";
import { Container, SectionHeader, MoreLink, Empty, Loading, SmartLink } from "../components/site/Common";
import { NewsCard, EventCard, AchievementCard } from "../components/site/Cards";
import { Lightbox } from "../components/site/Lightbox";
import { Welcome, FeaturedBearers, FeaturedPlayers, Sponsors } from "../components/site/HomeSections";

const Hero = ({ s }) => {
  const verified = s.affiliations?.filter((a) => a.verified) || [];
  const stats = [
    ...(s.established ? [{ icon: Calendar, v: `Est. ${s.established}`, l: "Association" }] : []),
    { icon: MapPin, v: "Thanjavur", l: "Tamil Nadu, India" },
    ...(verified.length ? [{ icon: ShieldCheck, v: verified.map((a) => a.key.toUpperCase()).join(" · "), l: "Officially affiliated" }] : []),
    { icon: Sparkles, v: "All ages", l: s.player_development },
  ];
  const cols = { 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4" }[stats.length];
  return (
    <section className="relative min-h-[92vh] flex items-end overflow-hidden bg-[#0B1F44]" data-testid="home-hero">
      {s.hero_image && <img src={mediaUrl(s.hero_image)} alt="Badminton player" className="absolute inset-0 h-full w-full object-cover opacity-60 hero-zoom" />}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B1F44] via-[#0B1F44]/80 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F44] via-transparent to-transparent" />
      <Container className="relative w-full pt-36 pb-16">
        <p className="inline-flex items-center gap-2 rounded-full bg-white/10 ring-1 ring-white/20 backdrop-blur px-4 py-1.5 text-xs font-semibold tracking-wider uppercase text-blue-100 reveal" data-testid="hero-badge">
          {s.established ? `Est. ${s.established} · ` : ""}Thanjavur, Tamil Nadu
        </p>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight text-white max-w-3xl leading-[1.05] reveal" style={{ animationDelay: "80ms" }} data-testid="hero-title">{s.hero_title}</h1>
        <p className="mt-6 text-base md:text-lg text-slate-200 max-w-xl leading-relaxed reveal" style={{ animationDelay: "160ms" }} data-testid="hero-subtitle">{s.hero_subtitle}</p>
        <div className="mt-9 flex flex-wrap gap-3 reveal" style={{ animationDelay: "240ms" }}>
          {s.hero_btn1_label && <SmartLink to={s.hero_btn1_link} className="btn-primary" testid="hero-explore-events-btn">{s.hero_btn1_label} <ArrowRight className="h-4 w-4" /></SmartLink>}
          {s.hero_btn2_label && <SmartLink to={s.hero_btn2_link} className="btn-ghost-light" testid="hero-about-btn">{s.hero_btn2_label}</SmartLink>}
        </div>
        <div className={`mt-16 grid grid-cols-2 ${cols} gap-px rounded-2xl overflow-hidden bg-white/10 ring-1 ring-white/15 backdrop-blur-md reveal`} style={{ animationDelay: "320ms" }} data-testid="hero-stats">
          {stats.map(({ icon: Icon, v, l }) => (
            <div key={l} className="bg-[#0B1F44]/50 p-5 md:p-6">
              <Icon className="h-5 w-5 text-blue-300 mb-3" />
              <p className="font-display text-white font-bold text-lg">{v}</p>
              <p className="text-xs text-slate-300 mt-1">{l}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

const NewsSection = ({ count }) => {
  const { items } = useContent("news");
  if (count <= 0) return null;
  return (
    <section className="py-20 md:py-28 bg-white" data-testid="home-news-section">
      <Container>
        <SectionHeader eyebrow="Latest news" title="What's happening at TDBA" text="Selections, results and announcements from the Thanjavur District Badminton Association." action={<MoreLink to="/news" testid="home-news-all-link">All news</MoreLink>} />
        {items && items.length > 0 && (
          <div className="mb-10 overflow-hidden rounded-full bg-slate-100 py-3 ticker-mask" data-testid="news-ticker">
            <div className="ticker flex gap-12 whitespace-nowrap text-sm font-medium text-[#0B1F44]">
              {[...items, ...items].slice(0, 12).map((n, i) => <span key={i} className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-blue-600" />{n.title}</span>)}
            </div>
          </div>
        )}
        {!items ? <Loading /> : items.length === 0 ? <Empty testid="news-empty">No news published yet.</Empty> : (
          <div className="grid gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3">{items.slice(0, count).map((n) => <NewsCard key={n.id} item={n} />)}</div>
        )}
      </Container>
    </section>
  );
};

export const Affiliations = ({ s }) => {
  const verified = s.affiliations?.filter((a) => a.verified) || [];
  if (!verified.length) return null;
  return (
    <section className="py-16 bg-slate-50 border-y border-slate-200" data-testid="home-affiliations-section">
      <Container>
        <p className="eyebrow text-center mb-8">Proudly affiliated</p>
        <div className="flex flex-wrap justify-center gap-6">
          {verified.map((a) => (
            <a key={a.key} href={a.url} target="_blank" rel="noreferrer" className="card flex items-center gap-5 px-7 py-5 max-w-md" data-testid={`affiliation-${a.key}`}>
              {a.logo ? <img src={mediaUrl(a.logo)} alt={a.name} className="h-16 w-16 object-contain" /> : <span className="h-16 w-16 rounded-full bg-blue-50 text-blue-700 grid place-items-center font-display font-bold">{a.key.toUpperCase()}</span>}
              <span>
                <span className="block font-display font-semibold text-[#0B1F44]">{a.name}</span>
                <span className="block text-sm text-slate-500 mt-1">{a.description}</span>
              </span>
              <ExternalLink className="h-4 w-4 text-slate-400 shrink-0" />
            </a>
          ))}
        </div>
      </Container>
    </section>
  );
};

export const QuickLinks = ({ s }) => (
  <section className="py-20 md:py-28 bg-slate-50" data-testid="home-quicklinks-section">
    <Container>
      <SectionHeader eyebrow="Quick links" title="Player resources" text="Official portals of the governing bodies, the state tournament calendar, and tools for draws, schedules and live results." />
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {s.quick_links?.map((q, i) => (
          <a key={i} href={q.url} target="_blank" rel="noreferrer" className="group card p-6 flex flex-col" data-testid={`quicklink-${i}`}>
            <span className="h-11 w-11 rounded-xl bg-[#0B1F44] text-white grid place-items-center mb-5 group-hover:bg-blue-600 transition-colors"><ExternalLink className="h-5 w-5" /></span>
            <h3 className="font-display font-semibold text-[#0B1F44]">{q.title}</h3>
            <p className="mt-2 text-sm text-slate-600 flex-1">{q.description}</p>
            <span className="mt-4 text-xs text-blue-700 font-semibold truncate">{q.url.replace(/^https?:\/\//, "")}</span>
          </a>
        ))}
      </div>
    </Container>
  </section>
);

const AboutSection = ({ s }) => {
  const cards = [
    { icon: GraduationCap, t: "Coaching & player development", d: s.card_coaching },
    { icon: Trophy, t: "Competitive badminton", d: s.card_competitive },
    { icon: Users, t: "Community participation", d: s.card_community },
  ];
  return (
    <section className="py-20 md:py-28 bg-white court-lines" data-testid="home-about-section">
      <Container>
        <SectionHeader eyebrow="Who we are" title="A Home for Every Badminton Player in Thanjavur" text={s.about_intro} action={<MoreLink to="/about" testid="home-about-more-link">More about TDBA</MoreLink>} />
        <div className="grid gap-6 md:grid-cols-3">
          {cards.map(({ icon: Icon, t, d }, i) => (
            <div key={t} className="card p-8 reveal" style={{ animationDelay: `${i * 90}ms` }} data-testid={`about-card-${i}`}>
              <span className="h-14 w-14 rounded-2xl bg-blue-50 text-blue-700 grid place-items-center mb-6"><Icon className="h-7 w-7" /></span>
              <h3 className="font-display text-xl font-semibold text-[#0B1F44]">{t}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl bg-[#0B1F44] text-white p-8"><p className="eyebrow !text-blue-300 mb-2">Mission</p><p className="leading-relaxed text-slate-200">{s.mission}</p></div>
          <div className="rounded-2xl bg-blue-600 text-white p-8"><p className="eyebrow !text-blue-100 mb-2">Vision</p><p className="leading-relaxed text-blue-50">{s.vision}</p></div>
        </div>
      </Container>
    </section>
  );
};

const EventsSection = ({ featured }) => {
  const { items } = useContent("events");
  const order = { Upcoming: 0, Ongoing: 0, Completed: 1 };
  const picked = items && featured?.length ? featured.map((id) => items.find((e) => e.id === id)).filter(Boolean) : null;
  const sorted = items ? picked || [...items].sort((a, b) => order[a.status] - order[b.status]).slice(0, 3) : null;
  return (
    <section className="py-20 md:py-28 bg-slate-50" data-testid="home-events-section">
      <Container>
        <SectionHeader eyebrow="What's on" title="Upcoming events & training" text="Coaching programmes, camps, district tournaments and association events for juniors and seniors." action={<MoreLink to="/events" testid="home-events-all-link">All events</MoreLink>} />
        {!sorted ? <Loading /> : sorted.length === 0 ? <Empty>No events yet.</Empty> : (
          <div className="grid gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3">{sorted.map((e) => <EventCard key={e.id} item={e} />)}</div>
        )}
      </Container>
    </section>
  );
};

const AchievementsSection = () => {
  const { items } = useContent("achievements");
  const [lb, setLb] = useState({ items: [], index: null });
  return (
    <section className="py-20 md:py-28 bg-white" data-testid="home-achievements-section">
      <Container>
        <SectionHeader eyebrow="Proud moments" title="Achievements & milestones" action={<MoreLink to="/achievements" testid="home-achievements-all-link">See all achievements</MoreLink>} />
        {!items ? <Loading /> : items.length === 0 ? <Empty>No achievements added yet.</Empty> : (
          <div className="grid gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3">{items.slice(0, 3).map((a) => <AchievementCard key={a.id} item={a} onPhotos={(p) => setLb({ items: p, index: 0 })} />)}</div>
        )}
      </Container>
      <Lightbox items={lb.items} index={lb.index} onClose={() => setLb({ items: [], index: null })} onIndex={(i) => setLb((x) => ({ ...x, index: i }))} />
    </section>
  );
};

export const CTA = ({ s }) => (
  <section className="py-20 md:py-24 bg-white" data-testid="home-cta-section">
    <Container>
      <div className="relative overflow-hidden rounded-3xl bg-[#0B1F44] px-8 py-16 md:px-16 md:py-20">
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-blue-600/40 blur-3xl" />
        <div className="absolute right-10 bottom-0 h-full w-1/3 court-lines-light opacity-40" />
        <div className="relative max-w-2xl">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight" data-testid="cta-title">{s.cta_title}</h2>
          <p className="mt-4 text-lg text-slate-300">{s.cta_text}</p>
          {s.cta_btn_label && <SmartLink to={s.cta_btn_link} className="mt-8 btn-primary" testid="cta-contact-btn">{s.cta_btn_label} <ArrowRight className="h-4 w-4" /></SmartLink>}
        </div>
      </div>
    </Container>
  </section>
);

export default function Home({ preview }) {
  const { settings } = useSite();
  const s = preview || settings;
  if (!s) return <div className="min-h-screen" />;
  return (
    <>
      <Hero s={s} />
      <Welcome s={s} />
      <NewsSection count={s.news_count ?? 3} />
      <Affiliations s={s} />
      <QuickLinks s={s} />
      <AboutSection s={s} />
      <EventsSection featured={s.featured_event_ids} />
      <FeaturedBearers ids={s.featured_bearer_ids} />
      <FeaturedPlayers ids={s.featured_player_ids} />
      <AchievementsSection />
      <Sponsors />
      <CTA s={s} />
    </>
  );
}
